import * as React from 'react';
import $ from 'jquery';
import { EventGroupType } from '../../Common/Constants/EventGroupType.Constants';
import { HttpResponse } from '../../Services/Services.Primary/HttpService/Models/HttpServiceModels';
import { PartnerSupportService } from '../../Services/Services.Primary/HttpService/PartnerSupportService';
import { AppSessionContext } from '../../Services/Services.Primary/SessionContext/AppSessionContext';
import TelemetryService from '../../Services/Services.Primary/Telemetry/TelemetryService';
import LocalizationService from '../../Services/Services.Secondary/Localization/LocalizationService';
import { Constants } from './Constants';
import { RecommendedTopics } from './Models/SupportTopic';
import { TopicSearchData } from './Models/TopicSearchData';
import { Helper } from './Utilities/Helper';
import { Stopwords } from './Utilities/Stopwords';

const Constant = Constants.getInstance();
const resx = LocalizationService.getInstance();
const tracingService = TelemetryService.getInstance();

const defaultResultDisplayCount = 15;
const defaultResultHeight = 40;
const browseTopicsDefaultHeight = 28;
const resultMargin = 10;
const defaultResultHeightWithDescription = 52;
const defaultMaxResultsHeight = 24 * defaultResultDisplayCount;
const Clear_selection_text = resx.getLocalizedResource('Clear_selection_text');
const ProblemSummary_text = resx.getLocalizedResource('ProblemSummary_text');
const ProblemType_text = resx.getLocalizedResource('ProblemType_text');
const NoSearchResults_html_text = resx.getLocalizedResource('NoSearchResults_html_text');
const NoMatches_html_text = resx.getLocalizedResource('NoMatchProblemType_html_text');
const NoMatches_html_text_inFocus = resx.getLocalizedResource('NoMatchProblemType_html_text_inFocus');
const Recommended_text = resx.getLocalizedResource('Recommended_text');
const DescribeYourIssueInShortSentence_text = resx.getLocalizedResource('DescribeYourIssueInShortSentence_text');
const SelectProblemTypeOrAddKeyword_text = resx.getLocalizedResource('SelectProblemTypeOrAddKeyword_text');
const EnterProblemSummary_text = resx.getLocalizedResource('EnterProblemSummary_text');
const Workspace_text = resx.getLocalizedResource('Workspace_Text');
const PartnerCenterWorkspace_text = resx.getLocalizedResource('PartnerCenterWorkspace_text');
const L1Category_text = resx.getLocalizedResource('L1Category_text');
const Other_text = resx.getLocalizedResource('OtherProgram');
const SelectAnOption_text = resx.getLocalizedResource('SelectAnOption_text');
let lanLocale = AppSessionContext.getInstance().Locale.split('-');
const lang = lanLocale && lanLocale.length > 0 ? lanLocale[0] : Constant.language.English;

export interface SupportTopicSearchProps {
  flatTopicTree: TopicSearchData[];
  selectedCategoryOrWorkspace: string;
  selectTopicCallback: (topicSearchData: TopicSearchData, verbatim: string, selectedRecommendedResult: boolean) => void;
  clearSearchCallback: () => void;
  setSearchBox: string;
  shouldSetSearchFromParent: boolean;
  incrementTopicSearchAttemptCount: () => void;
  showFilters: (newState: boolean) => void;
  verbatim: string;
  updateVerbatim: (verbatim: string) => void;
  workspaceDropdownEnabled: boolean;
  selectWorkspaceCallback: (event: React.FormEvent<EventTarget>) => void;
  workspaces: string[];
  otherWorkspaces: { [key: string]: string };
  includeUnauthTopics: boolean;
  setWorkspaceCallback: (workspace: string) => void;
  productFamilyId: string;
  workspaceToProductMapping: Map<string, string>;
  agentTopics: string[];
}

interface SupportTopicSearchState {
  searchInput: string;
  searchKeywords: Array<string>;
  results: TopicSearchData[];
  displaySearchBoxText: string;
  afterOnChange: boolean;
  selectedIndex: number;
  currentKey: string;
  listScrollPosition: number;
  resultIds: string[];
  noResultsOnBackspaceLogged: boolean;
  showResults: boolean;
  inputWidth: number;
  noMatchesText: string;
  tabKeyOnResults: boolean;
  recommendations: TopicSearchData[];
  problemSummaryError: string;
}

export default class SupportTopicSearch extends React.Component<SupportTopicSearchProps, SupportTopicSearchState> {
  private searchForm: React.RefObject<HTMLFormElement>;
  private searchBox: React.RefObject<HTMLInputElement>;
  private resultList: React.RefObject<HTMLUListElement>;
  private showResultsScroll: boolean;
  private areAllL2FilteredEqual: boolean;

  constructor(props: SupportTopicSearchProps) {
    super(props);

    this.searchBox = React.createRef();
    this.resultList = React.createRef();
    this.searchForm = React.createRef();
    this.showResultsScroll = false;
    this.areAllL2FilteredEqual = false;
    this.state = {
      searchInput: '',
      searchKeywords: new Array<string>(),
      results: new Array<TopicSearchData>(),
      displaySearchBoxText: '',
      afterOnChange: false,
      selectedIndex: -1,
      currentKey: '',
      listScrollPosition: 0,
      resultIds: new Array<string>(),
      noResultsOnBackspaceLogged: false,
      showResults: false,
      inputWidth: 800,
      noMatchesText: NoMatches_html_text,
      tabKeyOnResults: false,
      recommendations: new Array<TopicSearchData>(),
      problemSummaryError: '',
    };
    this.handleKeyPress = this.handleKeyPress.bind(this);
  }

  componentDidMount() {
    document.addEventListener('keydown', this.handleKeyPress);
    window.addEventListener('resize', this.handleResize);
    this.setInputWidth();
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleKeyPress);
    this.setState({ inputWidth: this.searchBox?.current?.clientWidth ?? 0 });
  }

  componentDidUpdate() {
    // if wayfinder's flight is on and
    // there is no search input and
    // all topics are passed to this component and
    // search result is not all topics then
    // set search result to all topics
    if (
      Helper.isEmptyOrSpaces(this.state.searchInput) &&
      this.props.flatTopicTree &&
      this.state.results.length < this.props.flatTopicTree.length
    ) {
      this.setState({ results: this.resetSearchResult() });
    }
  }

  private handleFocus = async (event: React.FocusEvent<HTMLInputElement>) => {
    await this.getRecommendedTopic();
    this.setState({ showResults: this.EnableProblemSummaryDropdown() });
  };

  private getCategoryMap = (): Map<string, number> => {
    let categoryMap = new Map<string, number>();

    categoryMap.set(Constant.ProgramId.CspProgramId, 0);
    categoryMap.set(Constant.ProgramId.IncentivesProgramId, 0);
    categoryMap.set(Constant.ProgramId.MpnProgramId, 0);
    categoryMap.set(Constant.ProgramId.IsvProgramId, 0);
    categoryMap.set(Constant.ProgramId.ReferralsProgramId, 0);
    categoryMap.set(Constant.ProgramId.GeneralProgramId, 0);

    return categoryMap;
  };

  private logSearchQuery = () => {
    if (this.state.afterOnChange) {
      let eventName = Constant.actionTypes.searchQueryEntered;

      let message = {
        searchInput: this.state.searchInput,
        cleanSearchKeywords: this.state.searchKeywords,
        numberOfResults: this.state.results.length,
        results: this.state.resultIds,
      };

      tracingService.traceEvent(eventName, message);
      this.props.incrementTopicSearchAttemptCount();
    }
  };

  private cleanSearchPhrase = (searchPhrase: string): string[] => {
    //cleans out arrows in case the user tries to include them in search, and for result auto population
    let arrows = />/g;
    let hyphenSpace = / - /g;
    let apostrophe = /'/g;
    let backslashes = /\\/g;

    let cleanPhrase = searchPhrase
      .toLowerCase()
      .replace(arrows, ' ')
      .replace(hyphenSpace, ' ')
      .replace(backslashes, '');
    cleanPhrase = Helper.cleanPunctuation(cleanPhrase.replace(apostrophe, ' '));

    let keywords = cleanPhrase.split(' ');

    return keywords;
  };

  private matchToken = (token: string, matchString: string) => {
    //if non-latin character use generic matching
    if ([...token].some(character => character.charCodeAt(0) > 127)) {
      return matchString.indexOf(token.toLowerCase()) != -1;
    } else {
      //if latin characters, can use regex to match from beginning of string
      const regex = new RegExp('\\b' + token.toLowerCase());
      return matchString.search(regex) !== -1;
    }
  };

  private setSearchResults = (searchPhrase: string) => {
    //count number of topics matching results for each category
    let categoryCount = this.getCategoryMap();

    //topics included in the list of search results
    let resultsList: TopicSearchData[] = [];

    //creating list of resultIds for logging
    let resultIds = new Array<string>();
    const keywordList = this.cleanSearchPhrase(searchPhrase);

    let incompleteWord = '';

    //don't apply stopword filter to words that are not complete- ex: 'cancel' will stop returnung results when user types 'can' but they will re-appear when the next letter typed, won't show results when users types word beginning with "a" or "to"
    // checking for keywordlist ending in empty string b/c that means there was a space there before the input was split on spaces.
    if (keywordList[keywordList.length - 1] != '') {
      incompleteWord = keywordList.pop() ?? '';
    }

    const keywords = Stopwords.RemoveStopwords(
      new Set(
        keywordList.filter(function (token) {
          return token.length !== 0 || !Helper.isEmptyOrSpaces(token);
        })
      )
    );

    if (!Helper.isEmptyOrSpaces(incompleteWord)) {
      keywords.add(incompleteWord);
    }

    this.setState({ searchKeywords: Array.from(keywords) });

    //don't bother storing topics with no matching keywords
    let maxScore = keywords.size > 1 ? 2 : 1;

    this.props.flatTopicTree.forEach(topic => {
      let score = 0;
      const searchTopicTags = Array.from(topic.searchTags).join(' ');

      Array.from(keywords).forEach((keyword: string) => {
        if (topic.searchTags.has(keyword) || this.matchToken(keyword, searchTopicTags)) {
          score++;
        }
      });

      if (score === maxScore) {
        categoryCount.set(topic.category, (categoryCount.get(topic.category) ?? 0) + 1);
        resultsList.push(topic);
        resultIds.push(topic.topicId);
      } else if (score > maxScore) {
        maxScore = score;
        resultsList = [topic];
        resultIds = [topic.topicId];

        //reset category count whenever a new max is found
        Array.from(categoryCount.keys()).forEach(key => {
          categoryCount.set(key, 0);
        });
      }
    });

    //sort topics by number of topics in their category, least -> most
    let sortedResultsList: TopicSearchData[] = resultsList.sort((a, b) => {
      if ((categoryCount.get(a.category) ?? 0) > (categoryCount.get(b.category) ?? 0)) return 1;
      if ((categoryCount.get(a.category) ?? 0) < (categoryCount.get(b.category) ?? 0)) return -1;

      //if categories have the same count, compare alphabetically
      if (a.category > b.category) return 1;
      if (a.category < b.category) return -1;

      return 0;
    });

    this.setState({ results: sortedResultsList, resultIds: resultIds });
  };

  private resetSearchResult = (): TopicSearchData[] => {
    return (this.props.flatTopicTree ? this.props.flatTopicTree : new Array<TopicSearchData>()) as TopicSearchData[];
  };

  private clearSearch = () => {
    this.setState({
      searchInput: '',
      results: this.resetSearchResult(),
      displaySearchBoxText: '',
      afterOnChange: false,
      selectedIndex: -1,
      listScrollPosition: 0,
    });
    this.searchForm?.current?.reset();
    this.props.clearSearchCallback();
    this.searchBox?.current?.focus();
  };

  public clearListHandler = (completeResultName: string) => {
    this.setState({
      searchInput: completeResultName,
      displaySearchBoxText: completeResultName,
      results: this.resetSearchResult(),
      selectedIndex: -1,
      listScrollPosition: 0,
    });
  };

  private handleReturnKeyForProblemSummaryTextField() {
    $(document).ready(function () {
      let focusables = $(':input').not('[type="image"]').not('[type="submit"]');

      focusables.keydown(function (e) {
        if (e.keyCode == 13 && this.id == 'problemSummary') {
          e.preventDefault();
          let current = focusables.index(this);
          let next = focusables.eq(current + 1).length ? focusables.eq(current + 1) : focusables.eq(0);
          next.focus();
        }
      });
    });
  }

  private handleRecommendationsChange = (e: React.FormEvent<EventTarget>) => {
    let target = e.target as HTMLInputElement;
    let errorMessage = Helper.ValidateTextBoxDefault(target.value);
    let shouldSetWorkspace =
      this.props.selectedCategoryOrWorkspace &&
      !Helper.isEmptyOrSpaces(this.props.selectedCategoryOrWorkspace) &&
      this.props.selectedCategoryOrWorkspace !== Constant.ProgramId.AllProgramsId;

    this.setState({
      searchInput: '',
      results: this.resetSearchResult(),
      displaySearchBoxText: shouldSetWorkspace
        ? Helper.getTagFromId(this.props.selectedCategoryOrWorkspace, this.props.workspaceDropdownEnabled)
        : '',
      afterOnChange: false,
      selectedIndex: -1,
      listScrollPosition: 0,
      problemSummaryError: errorMessage,
    });
    this.searchForm?.current?.reset();
    this.props.clearSearchCallback();
    this.props.updateVerbatim(target.value);
    if (shouldSetWorkspace) {
      this.props.setWorkspaceCallback(this.props.selectedCategoryOrWorkspace);
    }
    this.setState({ showResults: false });
    this.handleReturnKeyForProblemSummaryTextField();
  };

  private handleSearchChange = (e: React.FormEvent<EventTarget>) => {
    let target = e.target as HTMLInputElement;
    this.setState({ selectedIndex: -1, listScrollPosition: 0, showResults: true });

    if (Helper.isEmptyOrSpaces(target.value)) {
      this.clearListHandler('');
    } else {
      let previousSearchInput = this.state.searchInput;
      this.setState({ searchInput: target.value, displaySearchBoxText: target.value });
      this.setSearchResults(target.value);
      if (this.resultList.current) {
        this.resultList.current.scrollTop = 0;
      }

      //log search query for the first time the user backspaces when there are no results (resets if the user begins building query again)
      if (
        previousSearchInput.length > target.value.length &&
        this.state.results.length === 0 &&
        !this.state.noResultsOnBackspaceLogged
      ) {
        this.logSearchQuery();

        this.setState({ noResultsOnBackspaceLogged: true });
      } else if (previousSearchInput.length < target.value.length) {
        this.setState({ noResultsOnBackspaceLogged: false });

        //log each time a user adds a complete word to a query, not each letter they hadd
        if (target.value[target.value.length - 1] === ' ') {
          this.logSearchQuery();
        }
      }

      //this must come after logging calls
      this.setState({ afterOnChange: true });
    }
  };

  private traceRecommendationClick = (selectedResult: TopicSearchData) => {
    let eventName = Constant.actionTypes.selectSupportTopicFromRecommendation;
    let message = {
      verbatim: this.props.verbatim,
      supportTopicId: selectedResult.topicId.toString(),
      supportTopicName: selectedResult.label,
    };

    tracingService.traceEvent(eventName, message, EventGroupType.userInteraction);
  };

  private isRecommendedTopic = (selectedResult: TopicSearchData) => {
    for (let recommendation of this.state.recommendations) {
      if (recommendation.topicId === selectedResult.topicId) {
        return true;
      }
    }
    return false;
  };

  private handleResultClick = (index: number) => {
    this.logSearchQuery();

    const eventName = Constant.actionTypes.selectSupportTopicFromSearch;

    const filteredResults =
      this.state.results &&
      this.props.selectedCategoryOrWorkspace &&
      !Helper.isEmptyOrSpaces(this.props.selectedCategoryOrWorkspace) &&
      this.props.selectedCategoryOrWorkspace !== Constant.ProgramId.AllProgramsId
        ? this.state.results.filter(
            topic =>
              topic.workspaceId === this.props.selectedCategoryOrWorkspace ||
              topic.category === this.props.selectedCategoryOrWorkspace ||
              topic.L2.id === this.props.selectedCategoryOrWorkspace
          )
        : this.state.results;

    let selectedResult = filteredResults[index];
    let recommendationsLength = this.state.recommendations.length;
    if (index >= recommendationsLength) {
      selectedResult = filteredResults[index - recommendationsLength];
    } else {
      selectedResult = this.state.recommendations[index];
    }

    //log search input and available results at time of topic selection
    const message = {
      searchInput: this.state.searchInput,
      cleanSearchKeywords: this.state.searchKeywords,
      numberOfResults: filteredResults.length,
      results: this.state.resultIds,
      supportTopicId: selectedResult.topicId.toString(),
      supportTopicName: selectedResult.label,
    };

    tracingService.traceEvent(eventName, message, EventGroupType.userInteraction);

    let selectedRecommendedResult = false;
    if (this.isRecommendedTopic(selectedResult)) {
      this.traceRecommendationClick(selectedResult);
      selectedRecommendedResult = true;
    }

    this.clearListHandler(selectedResult.label);

    this.setState({
      displaySearchBoxText: this.filterTopicLabelByWorkspaceAndL2(selectedResult),
      afterOnChange: false,
      selectedIndex: index,
      showResults: false,
    });

    this.props.incrementTopicSearchAttemptCount();

    this.props.selectTopicCallback(selectedResult, this.props.verbatim, selectedRecommendedResult);
  };

  private handleRecommendationResultClick = (index: number) => {
    let selectedResult = this.state.recommendations[index];
    this.traceRecommendationClick(selectedResult);
    this.setState({
      displaySearchBoxText: this.filterTopicLabelByWorkspaceAndL2(selectedResult),
      afterOnChange: false,
      selectedIndex: -1,
      showResults: false,
    });
    this.props.selectTopicCallback(selectedResult, this.props.verbatim, true);
  };

  private setSearchBoxText = (): string => {
    if (this.props.shouldSetSearchFromParent && !this.state.afterOnChange) {
      return this.props.setSearchBox;
    } else {
      return this.state.displaySearchBoxText;
    }
  };

  private handleKeyPress = (e: any) => {
    this.setState({ currentKey: e.keyCode });

    let recommendationsLength = this.state.recommendations.length;
    let totalTopicsLengths = recommendationsLength + this.state.results.length;

    // key arrow up
    if (e.keyCode === 38 && this.state.selectedIndex > 0) {
      this.setState(prevState => ({
        selectedIndex: prevState.selectedIndex - 1,
      }));

      this.setState({ noMatchesText: NoMatches_html_text });

      const moveIndex =
        this.state.selectedIndex === totalTopicsLengths - 1 ? this.state.selectedIndex : this.state.selectedIndex + 1;

      const moveUp = this.resultList?.current?.childNodes[moveIndex] as HTMLLIElement;
      const nextItemHeight = moveUp.clientHeight;

      if (this.resultList && this.resultList.current && this.resultList.current.scrollTop > 0) {
        const scrollUpBy = -1 * (nextItemHeight + resultMargin);
        this.resultList.current.scrollTop = this.resultList.current.scrollTop + scrollUpBy;
      } else {
        this.setState(prevState => ({
          listScrollPosition: prevState.listScrollPosition - nextItemHeight - resultMargin,
        }));
      }
    } else if (e.keyCode === 40 && this.state.selectedIndex < totalTopicsLengths) {
      //if down arrow is pressed

      //increment selected index by 1 based on its previous value
      this.setState(prevState => ({
        selectedIndex: prevState.selectedIndex + 1,
      }));

      if (this.state.selectedIndex === totalTopicsLengths) {
        this.setState({ noMatchesText: NoMatches_html_text_inFocus });
      }

      //moveIndex is the number of items to scroll the results list view by. if at bottom of the list, scroll down by 0. else scroll down by 1 item
      const moveIndex =
        this.state.selectedIndex === totalTopicsLengths ? this.state.selectedIndex : this.state.selectedIndex + 1;

      const moveDown = this.resultList?.current?.childNodes[moveIndex] as HTMLLIElement;
      const nextItemHeight = moveDown.clientHeight;

      if (
        this.resultList &&
        this.resultList.current &&
        this.state.listScrollPosition >= this.resultList.current.clientHeight - (nextItemHeight + resultMargin)
      ) {
        this.resultList.current.scrollTop = this.resultList.current.scrollTop + (nextItemHeight + resultMargin);
      } else {
        this.setState(prevState => ({
          listScrollPosition: prevState.listScrollPosition + nextItemHeight + resultMargin,
        }));
      }
    } else if (
      e.keyCode === 13 &&
      this.state.selectedIndex >= 0 &&
      this.state.selectedIndex <= totalTopicsLengths &&
      this.state.showResults
    ) {
      //enter key

      //browse results item on the bottom is selected
      if (this.state.selectedIndex === totalTopicsLengths) {
        this.BrowseTopicsClick();
      } else {
        this.handleResultClick(this.state.selectedIndex);
      }
    } else if (e.keyCode === 9) {
      //tab key
      if (this.state.afterOnChange) {
        this.setState({ tabKeyOnResults: true });
        this.setState({ selectedIndex: totalTopicsLengths });
      }
    }
  };

  private handleBlur = () => {
    if (!Helper.isEmptyOrSpaces(this.state.searchInput)) this.logSearchQuery();
    this.setState({ afterOnChange: false });

    if (this.state.tabKeyOnResults) {
      this.setState({ tabKeyOnResults: false });
    } else {
      this.setState({ showResults: false });
    }
  };

  private setInputWidth = () => {
    if (this.searchBox && this.searchBox.current) {
      this.setState({ inputWidth: this.searchBox.current.clientWidth });
    }
  };

  private showRecommendations = (recommendationTopics: RecommendedTopics[]) => {
    let resultsList = new Array<TopicSearchData>();
    if (recommendationTopics.length > 0) {
      recommendationTopics.forEach((recommendedTopic: RecommendedTopics) => {
        this.props.flatTopicTree.forEach(topic => {
          if (topic.topicId == recommendedTopic.TopicIds) {
            if (
              topic.workspaceId == this.props.selectedCategoryOrWorkspace ||
              topic.L2.id == this.props.selectedCategoryOrWorkspace
            ) {
              resultsList.push(topic);
            }
          }
        });
      });
    } else {
      recommendationTopics = new Array<RecommendedTopics>();
    }
    if (resultsList.length > 3) {
      resultsList = resultsList.slice(0, 3);
    }
    let eventName = Constant.actionTypes.recommendationsApiReturn;

    let message = {
      verbatim: this.props.verbatim,
      acceptLanguage: lang,
      recommendationTopics: Array.from(resultsList, result => result.topicId),
    };

    tracingService.traceEvent(eventName, message, EventGroupType.api);
    this.setState({ recommendations: resultsList, showResults: true });
  };

  private getRecommendedTopic = async () => {
    if (!Helper.isEmptyOrSpaces(this.props.verbatim)) {
      if (Helper.isEmptyOrSpaces(encodeURIComponent(this.props.verbatim))) {
        //Adding logging of recommendations when encodeURIComponent of verbatim is blank

        let message = {
          verbatim: this.props.verbatim,
          acceptLanguage: lang,
          workspaceDropdownEnabled: this.props.workspaceDropdownEnabled,
        };

        tracingService.traceEvent(Constant.actionTypes.unAuthEmptyVerbatimAfterEncodingEvent, message);
      }
      this.setState({ recommendations: [], showResults: false });
      let productId = '';

      if (this.props.workspaceDropdownEnabled) {
        productId = this.props.workspaceToProductMapping.get(this.props.selectedCategoryOrWorkspace) ?? '';
        if (Helper.isEmptyOrSpaces(productId)) {
          productId = this.props.productFamilyId;
        }
      }

      await PartnerSupportService.getInstance()
        .getRecommendedTopics(this.props.verbatim, productId)
        .then((response: HttpResponse<RecommendedTopics[]>) => {
          this.showRecommendations(response.data);
        })
        .catch(error => {});
    } else {
      //Adding logging of recommendations
      let action = {
        actionType: Constant.TraceTypes.TrackEvent,
        tags: 'EmptyVerbatim',
      };

      let message = {
        verbatim: this.props.verbatim,
        acceptLanguage: lang,
        workspaceDropdownEnabled: this.props.workspaceDropdownEnabled,
      };

      tracingService.traceEvent(Constant.actionTypes.unAuthEmptyVerbatimEvent, message);
    }
  };

  private getRecommendationTag = (index: number) => {
    if (this.isRecommendedTopic(this.state.results[index])) {
      return (
        <span>
          &nbsp;&nbsp;{' '}
          <span
            style={{
              padding: '2px 8px 3px 8px',
              color: 'white',
              backgroundColor: '#0077b4',
              borderRadius: '50px',
              fontSize: '12px',
            }}
          >
            {Recommended_text}
          </span>
        </span>
      );
    }
  };

  private fontWeight = (item: number): number => (item === this.state.selectedIndex ? 520 : 400);

  private textDecoration = (item: number): string => (item === this.state.selectedIndex ? 'underline' : 'none');

  //this is used to calculate the height of the list of box holding the list of search results and indicate whether the combined height is large enough to set a max height and let the users scroll
  private getListHeight = (filteredResults: TopicSearchData[]): string => {
    let listHeight = 0;
    this.showResultsScroll = false;

    let recommendationsLength = this.state.recommendations.length;

    //if there are results to display
    if (filteredResults.length + recommendationsLength > 0) {
      //loop through results and use them to calculate total height of the results list
      //break if either all results have been added to the listHeight or the total height of the results exceeds the default max value for the list height

      //index of recommendations result
      let j = 0;
      if (recommendationsLength > 0) {
        // get height of recommendations title height
        listHeight +=
          document.getElementById('recommendations_title') === null
            ? 20 + resultMargin + 4
            : (document.getElementById('recommendations_title')?.clientHeight ?? 0) + resultMargin + 4;
      }
      while (j < recommendationsLength && listHeight < defaultMaxResultsHeight) {
        if (document.getElementById(this.state.recommendations[j].topicId + 'recommendations') !== null) {
          listHeight +=
            (document.getElementById(this.state.recommendations[j].topicId + 'recommendations')?.clientHeight ?? 0) +
            resultMargin;
        } else {
          //if listHeight for ul is being calculated before the li, use default values to display
          let hasDescription =
            this.state.recommendations[j].L3 !== null
              ? !Helper.isEmptyOrSpaces(this.state.recommendations[j].L3.description)
              : !Helper.isEmptyOrSpaces(this.state.recommendations[j].L2.description);
          listHeight += hasDescription ? defaultResultHeightWithDescription : 30;
        }

        j += 1;
      }

      //index of result
      let i = 0;

      if (filteredResults.length > 0) {
        // get height of all problem types title height
        listHeight +=
          document.getElementById('AllProblemTypes_title') === null
            ? 20 + resultMargin + 4
            : (document.getElementById('AllProblemTypes_title')?.clientHeight ?? 0) + resultMargin + 4;
      }
      while (i < filteredResults.length && listHeight < defaultMaxResultsHeight) {
        if (document.getElementById(filteredResults[i].topicId) !== null) {
          listHeight += (document.getElementById(filteredResults[i].topicId)?.clientHeight ?? 0) + resultMargin;
        } else {
          //if listHeight for ul is being calculated before the li, use default values to display
          let hasDescription =
            filteredResults[i].L3 !== null
              ? !Helper.isEmptyOrSpaces(filteredResults[i].L3.description)
              : !Helper.isEmptyOrSpaces(filteredResults[i].L2.description);
          listHeight += hasDescription ? defaultResultHeightWithDescription : 30;
        }

        i += 1;
      }

      //if there are more results to display than fit into the max list height then set the listHeightas the max
      //set shwResultsScroll to true so the y scroll bar will appear on the side of the results box
      if (i + j < filteredResults.length + recommendationsLength || listHeight > defaultMaxResultsHeight) {
        listHeight = defaultMaxResultsHeight;
        this.showResultsScroll = true;
      }

      //in addition to results, add extra pixels for the height f the 'browse topics' element that will appear at the bottom of the results
      listHeight +=
        document.getElementById('browse-topics-li') === null
          ? browseTopicsDefaultHeight
          : (document.getElementById('browse-topics-li')?.clientHeight ?? 0) + resultMargin;
    } else {
      //if there are no results for the search the listHeight is just the height of the 'no results' item
      listHeight +=
        document.getElementById('no-results-li') === null
          ? defaultResultHeight
          : document.getElementById('no-results-li')?.clientHeight ?? 0;
    }

    //convert number to string that can be used to set height of html element
    return listHeight.toString() + 'px';
  };

  private handleResize = () => {
    if (this.searchBox && this.searchBox.current && this.searchBox.current.clientWidth) {
      this.setState({ inputWidth: this.searchBox.current.clientWidth });
    }
  };

  private BrowseTopicsClick = () => {
    //this telemetry call must be made before resetting the afterOnchange value
    this.logSearchQuery();
    this.setState({ showResults: false, afterOnChange: false, noMatchesText: NoMatches_html_text });
    this.props.showFilters(true);

    const eventName = Constant.actionTypes.selectBrowseTopics;

    //log search input and available results at time of topic selection
    const message = {
      cleanSearchKeywords: this.state.searchKeywords,
      numberOfResults: this.state.results.length,
      results: this.state.resultIds,
    };

    tracingService.traceEvent(eventName, message, EventGroupType.userInteraction);
  };

  private DisplayTopicDescription = (topicSearchData: TopicSearchData, index: number) => {
    let topicDescription =
      topicSearchData.L3 !== null ? topicSearchData.L3.description : topicSearchData.L2.description;

    if (!Helper.isEmptyOrSpaces(topicDescription)) {
      return (
        <p
          className="results-list-item-description"
          style={{
            margin: '4px 0px 0px 0px',
            display: 'block',
            minHeight: '20px',
            paddingLeft: '0px',
            fontWeight: this.fontWeight(index),
            fontSize: '11px',
          }}
        >
          {topicDescription}
        </p>
      );
    }
  };

  private EnableProblemSummaryDropdown() {
    return (
      !Helper.isEmptyOrSpaces(this.props.verbatim) &&
      Helper.isEmptyOrSpaces(this.state.problemSummaryError) &&
      (this.props.workspaceDropdownEnabled ? !Helper.isEmptyOrSpaces(this.props.selectedCategoryOrWorkspace) : true)
    );
  }

  private displayCategoryOrWorkspaceLabel = () => {
    return this.props.includeUnauthTopics ? L1Category_text : Workspace_text;
  };

  private displayWorkspaceDropdown = () => {
    if (this.props.includeUnauthTopics) {
      return (
        <select
          className="form-control category-dropdown"
          aria-label={this.displayCategoryOrWorkspaceLabel()}
          onChange={e => this.props.selectWorkspaceCallback(e)}
          value={this.props.selectedCategoryOrWorkspace || ''}
        >
          <option disabled={true} selected={true} value="">
            {SelectAnOption_text}
          </option>
          <option disabled={true} value="" style={{ fontWeight: 'bold' }}>
            {PartnerCenterWorkspace_text}
          </option>
          {this.props.workspaces.map(workspaceId => (
            <option value={workspaceId}>{Helper.getTagFromId(workspaceId, this.props.workspaceDropdownEnabled)}</option>
          ))}
          <hr />
          <option disabled={true} value="" style={{ fontWeight: 'bold' }}>
            {Other_text}
          </option>
          {Object.keys(this.props.otherWorkspaces).map(workspaceId => (
            <option value={workspaceId}>{this.props.otherWorkspaces[workspaceId]}</option>
          ))}
        </select>
      );
    } else {
      return (
        <select
          className="form-control category-dropdown"
          aria-label={this.displayCategoryOrWorkspaceLabel()}
          onChange={e => this.props.selectWorkspaceCallback(e)}
          value={this.props.selectedCategoryOrWorkspace || ''}
        >
          <option disabled={true} selected={true} value="">
            {SelectAnOption_text}
          </option>
          {this.props.workspaces.map(workspaceId => (
            <option value={workspaceId}>{Helper.getTagFromId(workspaceId, this.props.workspaceDropdownEnabled)}</option>
          ))}
          {this.props.agentTopics.map(workspace => (
            <option value={workspace}>{workspace}</option>
          ))}
        </select>
      );
    }
  };

  private filterTopicLabelByWorkspaceAndL2 = (topic: TopicSearchData) => {
    if (!this.props.workspaceDropdownEnabled || Helper.isEmptyOrSpaces(this.props.selectedCategoryOrWorkspace))
      return topic.label;

    let filterLabelBy = topic.workspaceName + ' > ';
    if (topic.L2 !== null && this.areAllL2FilteredEqual && topic.workspaceName != '') {
      filterLabelBy += topic.L2.name + ' > ';
    }
    return topic.label.replace(filterLabelBy, '');
  };

  private areAllL2Equal = (filteredFlatTree: TopicSearchData[]) => {
    //Assuming that if all topics are available theres no way that all L2's match
    if (!this.props.workspaceDropdownEnabled || Helper.isEmptyOrSpaces(this.props.selectedCategoryOrWorkspace))
      return false;

    let baseL2Name: string | null = null;
    for (var topic of filteredFlatTree) {
      if (baseL2Name === null) {
        baseL2Name = topic.L2.name;
      } else if (baseL2Name !== topic.L2.name) {
        return false;
      }
    }
    return true;
  };

  private getFilteredTopics = () => {
    const filteredTopics =
      this.state.results &&
      this.props.selectedCategoryOrWorkspace &&
      !Helper.isEmptyOrSpaces(this.props.selectedCategoryOrWorkspace) &&
      this.props.selectedCategoryOrWorkspace !== Constant.ProgramId.AllProgramsId
        ? this.state.results.filter(
            topic =>
              topic.workspaceId === this.props.selectedCategoryOrWorkspace ||
              topic.category === this.props.selectedCategoryOrWorkspace ||
              topic.L2.id === this.props.selectedCategoryOrWorkspace
          )
        : this.state.results;
    this.areAllL2FilteredEqual = this.areAllL2Equal(filteredTopics);
    return filteredTopics;
  };

  private getProblemTypesHeaderFromId = (workspaceId: string) => {
    return Helper.getProblemTypeHeaderFromId(workspaceId, this.props.workspaceDropdownEnabled);
  };

  private LoadResults() {
    const recommendationsLength = this.state.recommendations.length;
    const filteredTopics = this.getFilteredTopics();
    const listHeight = this.getListHeight(filteredTopics);

    if (this.state.results.length + recommendationsLength > 0 && this.state.showResults) {
      return (
        <ul
          className="topic-search-results"
          ref={this.resultList}
          style={{
            paddingTop: '0px',
            height: listHeight,
            overflowY: this.showResultsScroll ? 'scroll' : 'hidden',
            width: this.state.inputWidth + 'px',
            overflowX: 'hidden',
            zIndex: 999,
            position: 'absolute',
          }}
        >
          {recommendationsLength > 0 && (
            <li id="recommendations_title" style={{ margin: '4px 0px 8px 0px' }}>
              {' '}
              <p
                className="results-list-item"
                style={{ margin: '0px', display: 'inline-block', minHeight: '20px', paddingLeft: '4px' }}
              >
                <b>{Recommended_text}</b>{' '}
              </p>
            </li>
          )}
          {recommendationsLength > 0 &&
            this.state.recommendations.map((listValue: TopicSearchData, index: number) => (
              <li id={listValue.topicId + 'recommendations'} style={{ margin: '0px 0px 10px 0px' }}>
                {' '}
                <a
                  className="results-list-item"
                  style={{
                    margin: '0px',
                    display: 'inline-block',
                    minHeight: '20px',
                    paddingLeft: '4px',
                    fontWeight: this.fontWeight(index),
                    textDecoration: this.textDecoration(index),
                  }}
                  onMouseDown={() => this.handleRecommendationResultClick(index)}
                >
                  {' '}
                  {this.filterTopicLabelByWorkspaceAndL2(listValue)}
                  {this.DisplayTopicDescription(listValue, index)}
                </a>
              </li>
            ))}
          {recommendationsLength > 0 && this.state.results.length > 0 && (
            <li>
              <hr />
            </li>
          )}
          {this.state.results.length > 0 && (
            <li id="AllProblemTypes_title" style={{ margin: '4px 0px 8px 0px' }}>
              {' '}
              <p
                className="results-list-item"
                style={{ margin: '0px', display: 'inline-block', minHeight: '20px', paddingLeft: '4px' }}
              >
                <b>{this.getProblemTypesHeaderFromId(this.props.selectedCategoryOrWorkspace)}</b>{' '}
              </p>
            </li>
          )}
          {filteredTopics.map((listValue: TopicSearchData, index: number) => (
            <li id={listValue.topicId} style={{ margin: '0px 0px 10px 0px' }}>
              <a
                className="results-list-item"
                style={{
                  margin: '0px',
                  display: 'inline-block',
                  minHeight: '20px',
                  paddingLeft: '4px',
                  fontWeight: this.fontWeight(index + recommendationsLength),
                  textDecoration: this.textDecoration(index + recommendationsLength),
                }}
                onMouseDown={() => this.handleResultClick(index + recommendationsLength)}
              >
                {' '}
                {this.filterTopicLabelByWorkspaceAndL2(listValue)}
                {this.getRecommendationTag(index)}{' '}
                {this.DisplayTopicDescription(listValue, index + recommendationsLength)}
              </a>
            </li>
          ))}
          <li
            id="browse-topics-li"
            style={{ margin: '0px 0px 10px 0px', paddingLeft: '4px', fontWeight: 400 }}
            onMouseDown={this.BrowseTopicsClick}
            dangerouslySetInnerHTML={{ __html: this.state.noMatchesText }}
          />
        </ul>
      );
    } else if (!Helper.isEmptyOrSpaces(this.state.searchInput) && this.state.showResults) {
      tracingService.traceEvent(Constant.actionTypes.clientSideProblem, { errorMessage: 'NoSearchResults_html_text' });
      return (
        <ul
          className="topic-search-results"
          style={{
            paddingTop: '0px',
            overflowY: 'hidden',
            overflowX: 'hidden',
            width: this.searchBox?.current?.clientWidth.toString() + 'px',
            zIndex: 999,
            position: 'absolute',
          }}
        >
          <li
            id="no-results-li"
            style={{ margin: '4px 0px 4px 0px', paddingLeft: '4px', fontWeight: 400 }}
            onMouseDown={this.BrowseTopicsClick}
            dangerouslySetInnerHTML={{ __html: NoSearchResults_html_text }}
          />
        </ul>
      );
    }
  }

  public render() {
    let display = (
      <div>
        <div>
          <form
            style={{ marginBottom: '35px' }}
            onSubmit={e => {
              e.preventDefault();
            }}
          >
            <label className="form-label required" style={{ marginBottom: '11px' }}>
              {ProblemSummary_text}
            </label>
            <input
              id="problemSummary"
              style={{ marginRight: '12px', maxWidth: '800px' }}
              autoComplete="off"
              type="text"
              className="form-control"
              placeholder={DescribeYourIssueInShortSentence_text}
              onChange={e => this.handleRecommendationsChange(e)}
              value={this.props.verbatim}
              aria-label={ProblemSummary_text}
              aria-required="true"
            ></input>
            <span className="errorMessage">{this.state.problemSummaryError}</span>
          </form>
          {this.props.workspaceDropdownEnabled && (
            <div className="dropdown" style={{ marginBottom: '35px', maxWidth: '800px' }}>
              <label className="form-label required" style={{ marginBottom: '11px' }}>
                {this.displayCategoryOrWorkspaceLabel()}
              </label>
              <div className="combobox">{this.displayWorkspaceDropdown()}</div>
            </div>
          )}
          <form
            onBlur={() => this.handleBlur()}
            ref={this.searchForm}
            onSubmit={e => {
              e.preventDefault();
            }}
          >
            <label className="form-label required" style={{ marginBottom: '11px' }}>
              {ProblemType_text}
            </label>{' '}
            <br />
            {this.EnableProblemSummaryDropdown() && (
              <input
                ref={this.searchBox}
                autoComplete="off"
                id="topicSearch"
                type="text"
                onFocus={e => this.handleFocus(e)}
                className="form-control"
                placeholder={SelectProblemTypeOrAddKeyword_text}
                onChange={e => this.handleSearchChange(e)}
                name="searchTerm"
                value={this.setSearchBoxText()}
                aria-label={ProblemType_text}
                aria-required="true"
              ></input>
            )}
            {!this.EnableProblemSummaryDropdown() && (
              <input
                disabled
                ref={this.searchBox}
                autoComplete="off"
                id="topicSearch"
                type="text"
                onFocus={e => this.handleFocus(e)}
                className="form-control"
                placeholder={EnterProblemSummary_text}
                onChange={e => this.handleSearchChange(e)}
                name="searchTerm"
                value={this.setSearchBoxText()}
                aria-label={ProblemType_text}
                aria-required="true"
              ></input>
            )}
            {this.LoadResults()}
            <button id="clearSearchButton" className="btn-primary" type="button" onClick={() => this.clearSearch()}>
              {Clear_selection_text}
            </button>
          </form>
        </div>
      </div>
    );
    return display;
  }
}
